import type {
  ProductQuantity,
  DesignTemplates,
  PriceVariance,
  Page,
  PageOrientationType,
  PageOrientationDimensions,
  DesignMarkUpSettings,
} from '@zoomcatalog/design-templates';

export const DEFAULT_PAGE_DIMENSIONS: [number, number] = [816, 1056];

export interface ILayout {
  layout_id: string;
  logos_slots_quantity: number;
  name: DesignTemplates;
  products_slots_quantity: number;
}

export interface ILogo {
  created_at: string;
  is_active: boolean;
  is_deleted: boolean;
  logo_id: string;
  page_id: string;
  project_id: string;
  slot: number;
  updated_at: string;
  url: string;
}

export type IProduct = {
  slot: number;
  project_id: string;
  page_id: string;
  product_id: string;
  sku?: string;
  image_url?: string;
  currency?: string;
  style_id: string;
  style_name: string;
  supplier_id: string;
  part_id: string;
  name: string;
  product_name: string;
  product_name_raw: string;
  product_url: string;
  description: string;
  brand: string;
  category: string;
  source: string;
  materials: string;
  color: string;
  sizes: string[];
  swatch: string | string[];
  swatch_image: string;
  image: string | null;
  image_large: string | null;
  price: number;
  pricing: {
    as_low_as: {
      USD: number;
      CAD: number;
      GBP: number;
      [key: string]: number;
    };
  };
  pricing_code: string;
  price_code: {
    USD: string | null;
    CAD: string | null;
    GBP: string | null;
    [key: string]: string | null;
  };
  variations: IProductVariation[];
  quantities: ProductQuantity[];
  price_variance: PriceVariance[];
  data: ProductData;
};

export interface IProductVariation {
  color: string;
  sizes: string[];
  image: string;
  image_large: string;
  material: string;
  part_id: string;
  price_code: { USD: string | null; CAD: string | null; GBP: string | null };
  pricing: { as_low_as: { USD: number; CAD: number; GBP: number } };
  swatch: string | string[];
  swatch_image: string;
  variation_id: number;
}

// Settings before @zoomcatalog/design-templates package
type OldProductCardSettings = Partial<{
  product_id: boolean;
  style_id: boolean;
  product_image: boolean;
  product_name: boolean;
  description: boolean;
  materials: boolean;
  colors: boolean;
  sizes: boolean;
  pricing: boolean;
  moq: boolean;
  website: boolean;
}>;

export type CanvasSettings = OldProductCardSettings;

export type OldTemplateSettings = {
  page?: {
    footer?: boolean;
    disclaimerText?: boolean;
    cover_image?: boolean;
    products?: boolean;
    title?: boolean;
    logos?: boolean;
  };
  canvas?: CanvasSettings;
  sidebar?: OldProductCardSettings & { product_url?: boolean };
};

export interface IPage {
  created_at: string;
  entity_type: 'page';
  index: number;
  templateName: string;
  layout: ILayout;
  layout_id?: string;
  layoutId: string;
  logos: Record<string, ILogo | undefined>;
  pageId: string;
  page_id?: string;
  products: Record<string, IProduct | undefined>;
  projectId: string;
  project_id: string;
  settings?: Partial<Page['settings']>;
  status: 'active';
  updated_at: string;
  cover_image: string;
  coverImage: string;
  title: string;
}

export interface ISimplePage extends IPage {
  currency: IDesign['currency'];
  product_id_field: IDesign['product_id_field'];
}

export type BasicPage = Pick<IPage, 'index' | 'page_id'> & {
  spread_page?: Page['settings']['feature']['spreadPage'];
};

export type IDesign = {
  company_id: string;
  created_at: string;
  currency: string;
  is_draft: boolean;
  is_published: boolean;
  is_template: boolean | null;
  name: string;
  pages: BasicPage[];
  product_id_field: 'part_id' | 'style_id' | 'style_name';
  project_id: string;
  short_id: string;
  parent: string | null;
  status: string;
  updated_at: string;
  user: string;
  user_id: string;
  settings: IDesignSettings;
};

export type IDesignType = {
  name: PageOrientationType;
  dimensions: PageOrientationDimensions;
  thumbnail: string;
};

export type IDesignSettings = {
  pricingSettings: IPricingSettings;
  designMarkup?: DesignMarkUpSettings;
  designType?: Omit<IDesignType, 'thumbnail'>;
  autoLogo?: boolean;
  autoLogoData?: {
    assetId: string;
    url: string;
  };
  virtuals?: boolean;
  virtualsLogo?: {
    assetId?: string;
    url?: string;
    isExternal?: boolean;
    parentId?: string;
    name?: string;
  };
};

export interface IPricingSettings {
  mark_up?: string;
  price_type?: string;
  price_label?: string;
  config_type?: string;
  custom_mark_up?: string;
}

export type ProductData = {
  data: IProduct;
};

export interface IUser {
  id: string;
  added_by: string;
  avatar: string;
  client_id: string;
  created_at: string;
  email: string;
  firstname: string;
  lastname: string;
  profile_id: number;
  role_name: string;
  uid: string;
  updated_at: string;
  username: string;
}

export type Company = {
  company_id: string;
  description: string;
  is_active: boolean;
  is_confirmed: boolean;
  is_merged: boolean;
  is_public: boolean;
  is_supplier: boolean;
  is_zoomcatalog: boolean;
  name: string;
  slug: string;
  typ: string;
  created_at: string;
  updated_at: string;
};

export interface Event<T extends string> {
  type: T;
}

export interface EventPayload<T extends string, P> extends Event<T> {
  payload: P;
}
